import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  convertExpiresInToExpiresAt,
  setAuthCookie,
} from '@/utilities/cookies';
import { produce } from 'immer';
import { AUTH_SCOPE, SWS_CLIENT_ID } from '@/constants/env';
import type { ProfileState } from '@/hooks/useUser';
import { getDefaultProfileState, QUERY_KEY as USER_KEY } from '@/hooks/useUser';
import { useCoupon } from '@/hooks/useCoupon';
import { userRegister } from '@simplywallst/services';
import type { ReferralData } from '@/components/AuthenticationSelect/types';
import {
  NOT_FOUND,
  actionToPath,
  pathToAction,
  redirect,
} from 'redux-first-router';
import {
  ROUTE_ABOUT,
  ROUTE_ACCOUNT_VALIDATION,
  ROUTE_ACCOUNT_VALIDATION_LOCALE,
  ROUTE_HOME,
  ROUTE_PLANS,
  ROUTE_STOCKS_HOME,
  ROUTE_USER_REGISTER,
  ROUTE_USER_REGISTER_LOCALE,
  ROUTE_WELCOME,
  ROUTE_WELCOME_LOCALE,
} from '@/constants/routes';
import { useLocation } from '@/hooks/useLocation';
import queryString from 'query-string';
import { useMemo } from 'react';
import { useBrokerToken } from '@/hooks/useBrokerToken';
import { useLocale } from '@/hooks/useLocale';
import { useUserLocale } from '@/hooks/useUserLocale';
import routesGenerator from '@/router/routesGenerator';

type CreateAccountParams = {
  email: string;
  password: string;
  firstName: string;
  referralData?: ReferralData;
  /**
   * Redirect to this route after successful registration.
   */
  redirectTo?: string;
};

export function useRegister({
  onSuccess = () => undefined,
  onError,
}: {
  onSuccess?: (response: Awaited<ReturnType<typeof userRegister>>) => void;
  onError?: (error: unknown) => void;
}) {
  const routeLocale = useLocale();
  const [userLocale] = useUserLocale();

  const inferredLocale = useMemo(() => {
    /**
     * prefer current route locale if it's not default (en)
     *
     * otherwise read persistent user locale
     *
     **/
    return routeLocale !== 'en' ? routeLocale : userLocale;
  }, [routeLocale, userLocale]);

  const brokerToken = useBrokerToken();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { registerCoupon } = useCoupon();

  const location = useLocation();
  const { type } = location;

  const redirectTo = useMemo(() => {
    /** Do not pass a `redirect_to` param when user is on these routes */
    if (
      [ROUTE_HOME, ROUTE_USER_REGISTER, ROUTE_USER_REGISTER_LOCALE].includes(
        location.type
      )
    ) {
      return undefined;
    }

    return actionToPath(location, location.routesMap, queryString);
  }, [location]);

  return useMutation<
    [Awaited<ReturnType<typeof userRegister>>, CreateAccountParams],
    unknown,
    CreateAccountParams
  >({
    mutationFn: async (params) => {
      const response = await userRegister({
        client_id: SWS_CLIENT_ID,
        email: params.email,
        name: params.firstName,
        password: params.password,
        scope: AUTH_SCOPE,
        source: 'react',
        referral_data: JSON.stringify(params.referralData),
        validation: true,
        redirect_to: params.redirectTo ?? redirectTo,
        ...(brokerToken !== '' ? { broker_token: brokerToken } : undefined),
        preferred_locale: inferredLocale,
      });
      return [response, params];
    },
    onSuccess: async ([response, params]) => {
      const { data: responseData } = response.data;
      if (responseData.registration_status === 'pending') {
        /** validation flow */
        dispatch(
          inferredLocale === 'en'
            ? redirect({
                type: ROUTE_ACCOUNT_VALIDATION,
                payload: { email: responseData.email },
                query: { userId: responseData.id },
              })
            : redirect({
                type: ROUTE_ACCOUNT_VALIDATION_LOCALE,
                payload: {
                  locale: inferredLocale,
                  email: responseData.email,
                },
                query: { userId: responseData.id },
              })
        );
      } else {
        /** legacy flow */
        const [responseToken] = responseData.token;
        try {
          const { access_token, refresh_token, expires_in } = responseToken;
          const expiresAt = convertExpiresInToExpiresAt(expires_in);
          setAuthCookie(access_token, refresh_token, expiresAt);
        } catch (e) {
          console.error(e);
        }
        /** Preset some user data prior to registration transition */
        queryClient.setQueryData<ProfileState>(
          USER_KEY,
          (state = getDefaultProfileState()) =>
            produce(state, (draft) => {
              draft.emailAddress = responseData.email;
              draft.countryISO = responseData.country_iso;
              return draft;
            })
        );

        onSuccess(response);

        /** register any coupon from persistent storage upon registration */
        registerCoupon();

        /** and finally redirect */

        if (typeof params.redirectTo === 'string') {
          // r query string exist
          const routesMap = routesGenerator();
          const action = pathToAction(
            params.redirectTo,
            routesMap,
            queryString
          );
          if (action.type !== NOT_FOUND) {
            dispatch(
              redirect({
                // typing is complaining when passing the entire action object
                type: action.type,
                payload: action.payload,
                // https://github.com/faceyspacey/redux-first-router/blob/master/src/flow-types.js#L173
                // https://github.com/faceyspacey/redux-first-router/blob/master/src/pure-utils/pathToAction.js
                // @ts-expect-error action.meta is an object
                query: action.query ?? action.meta?.query ?? {},
              })
            );
          }
        } else if (
          [
            ROUTE_HOME,
            ROUTE_WELCOME,
            ROUTE_WELCOME_LOCALE,
            ROUTE_PLANS,
            ROUTE_ABOUT,
            ROUTE_USER_REGISTER,
            ROUTE_USER_REGISTER_LOCALE,
          ].includes(type)
        ) {
          dispatch(redirect({ type: ROUTE_STOCKS_HOME }));
        }
      }
    },
    onError(error) {
      if (typeof onError === 'function') {
        onError(error);
      }
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: USER_KEY });
    },
  });
}
